import { Link } from "@remix-run/react";
import { Search, User, Menu, Download, Star, ChevronDown, LogOut, Upload, Heart } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { LanguageSwitcher } from "~/components/ui/language-switcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { useCurrentLanguage, createLocalizedPath } from "~/lib/language";
import { useUser } from "~/lib/user-context";
import { useState, useRef, useEffect } from "react";

// 添加Discord图标组件
function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9460 2.4189-2.1568 2.4189Z"/>
    </svg>
  );
}

export function Header() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { languagePrefix } = useCurrentLanguage();
  const { user, dbUser, isFullyRegistered, loading, signOut } = useUser();
  


  // 点击外部关闭下拉菜单
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo and Brand */}
        <div className="flex items-center space-x-4">
          <Link to={languagePrefix || "/"} className="flex items-center space-x-2">
            <img src="/favicon.ico" alt="Stellar Blade Mods" className="h-8 w-8" />
            <span className="text-xl font-bold">Stellar Blade Mods</span>
          </Link>
          
          {/* Discord社群邀请 - 紧挨着logo */}
          <Button variant="ghost" size="sm" asChild>
            <a 
              href="https://discord.gg/daYR38qPB2" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-1"
            >
              <DiscordIcon className="h-4 w-4" />
              <span className="hidden sm:inline">SBMods on Discord</span>
            </a>
          </Button>
        </div>

        {/* Main Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          {/* Mods Link */}
          <Link 
            to={createLocalizedPath('/mods', languagePrefix)} 
            className="text-sm font-medium transition-colors hover:text-primary px-3 py-2 rounded-md hover:bg-accent"
          >
            Mods
          </Link>

          {/* Community 下拉菜单 - 使用JavaScript控制 */}
          <div className="relative" ref={dropdownRef}>
            <button 
              className="flex items-center space-x-1 text-sm font-medium transition-colors hover:text-primary px-3 py-2 rounded-md hover:bg-accent"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              onMouseEnter={() => setIsDropdownOpen(true)}
            >
              <span>Community</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* 下拉菜单 - 使用JavaScript显示/隐藏 */}
            {isDropdownOpen && (
              <div 
                className="absolute top-full left-0 mt-1 w-64 bg-popover border border-border rounded-md shadow-lg z-50"
                onMouseLeave={() => setIsDropdownOpen(false)}
              >
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Community Section */}
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Community
                      </h4>
                      <div className="space-y-2">
                        <Link 
                          to={createLocalizedPath('/wiki', languagePrefix)} 
                          className="block p-2 rounded hover:bg-accent transition-colors text-sm"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          Wiki
                        </Link>
                        <a 
                          href="https://discord.gg/daYR38qPB2" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 p-2 rounded hover:bg-accent transition-colors text-sm"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <DiscordIcon className="h-4 w-4" />
                          <span>Discord</span>
                        </a>
                      </div>
                    </div>

                    {/* News Section */}
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        News
                      </h4>
                      <div className="space-y-2">
                        <Link 
                          to={createLocalizedPath('/news/game', languagePrefix)} 
                          className="block p-2 rounded hover:bg-accent transition-colors text-sm"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          Game News
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* About Link */}
          <Link 
            to={createLocalizedPath('/about', languagePrefix)} 
            className="text-sm font-medium transition-colors hover:text-primary px-3 py-2 rounded-md hover:bg-accent"
          >
            About
          </Link>

          {/* 暂时注释掉其他导航链接 */}
          {/* <Link 
            to="/mods/new" 
            className="text-sm font-medium transition-colors hover:text-primary"
          >
            New
          </Link>
          <Link 
            to="/mods/trending" 
            className="text-sm font-medium transition-colors hover:text-primary"
          >
            Trending
          </Link>
          <Link 
            to="/mods/popular" 
            className="text-sm font-medium transition-colors hover:text-primary"
          >
            Popular
          </Link>
          <Link 
            to="/mods/surprise" 
            className="text-sm font-medium transition-colors hover:text-primary"
          >
            Surprise
          </Link> */}
        </nav>

        {/* Search Bar - 暂时注释掉 */}
        {/* <div className="hidden md:flex items-center space-x-2 flex-1 max-w-sm mx-6">
          <div className="relative w-full">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search mods..."
              className="pl-8"
            />
          </div>
        </div> */}

        {/* User Actions */}
        <div className="flex items-center space-x-2">
          {/* Language Switcher */}
          <LanguageSwitcher />
          
          {/* Share Button */}
          <Button variant="default" size="sm" asChild className="bg-green-600 hover:bg-green-700 text-white">
            <Link to="/mods/upload" className="flex items-center space-x-1">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Share</span>
            </Link>
          </Button>
          
          {/* Upload Button - 暂时注释掉 */}
          {/* <Button variant="ghost" size="sm" asChild>
            <Link to="/upload">
              <Download className="h-4 w-4 mr-1" />
              Upload
            </Link>
          </Button> */}

          {/* User Menu */}
          {loading ? (
            <Button variant="ghost" size="sm" disabled>
              <User className="h-4 w-4 animate-pulse" />
            </Button>
          ) : isFullyRegistered ? (
            <div className="relative group">
              <button className="flex items-center space-x-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                <img
                  className="h-8 w-8 rounded-full object-cover"
                  src={dbUser.avatar_url || '/avatar.webp'}
                  alt={dbUser.display_name || dbUser.username}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/avatar.webp';
                  }}
                />
                <span className="hidden md:block text-white">{dbUser.display_name || dbUser.username}</span>
                <ChevronDown className="h-4 w-4 text-gray-300" />
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <Link
                  to="/user/profile"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </Link>
                <Link
                  to="/mods/upload"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Share MODs
                </Link>
                <Link
                  to="/user/favorites"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Heart className="h-4 w-4 mr-2" />
                  Favorites
                </Link>
                <div className="border-t border-gray-100 my-1"></div>
                <button
                  onClick={signOut}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            <Button 
              onClick={() => window.location.href = '/auth/login'}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Sign In
            </Button>
          )}

          {/* Mobile Menu - 暂时注释掉 */}
          {/* <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden">
                <Menu className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem asChild>
                <Link to="/mods/new">New</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/mods/trending">Trending</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/mods/popular">Popular</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/mods/surprise">Surprise</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu> */}
        </div>
      </div>
    </header>
  );
} 